// Currencies verified via Storefront localization (@inContext country) 2026-10-08.
// Market = Shopify Markets context. Each market maps to a country code,
// display currency, and label. Shopify returns prices already converted
// (and per-market overrides if you set them in Shopify Admin → Markets).
//
// نخدم حالياً ٥ دول: السعوديّة، الإمارات، العراق، عُمان، الولايات المتحدة.
// أيّ بلد آخر → ROW بالدولار للاطّلاع فقط — بدون شحن.

import { ADDED_SHIPPING_COUNTRIES, type AddedShippingCountry } from "@/lib/international-shipping";
import { getCountryCallingCode, isSupportedCountry } from "libphonenumber-js";

export type MarketId = "SA" | "AE" | "KW" | "QA" | "BH" | "OM" | "IQ" | "US" | "ROW" | AddedShippingCountry;

export interface Market {
  id: MarketId;
  /** ISO 3166-1 alpha-2 country code passed to Shopify @inContext */
  countryCode: string;
  currency: string;
  flag: string;
  /** رمز الاتّصال الدوليّ بدون + (مثلاً "971" لـ AE). null لـ ROW. */
  dialCode: string | null;
  /** هل ندعم الشحن إلى هذا السوق؟ ROW → false (عرض فقط). */
  supportsShipping: boolean;
  labels: { en: string; ar: string; fr: string };
}

export const SUPPORTED_MARKET_IDS: MarketId[] = ["SA", "AE", "KW", "QA", "BH", "OM", "IQ", "US", ...ADDED_SHIPPING_COUNTRIES];

// Keep ISO country identity distinct; Draft markets have no guessed display currency.
const addedMarkets = Object.fromEntries(ADDED_SHIPPING_COUNTRIES.map((code) => {
  const en = new Intl.DisplayNames(["en"], { type: "region" }).of(code) ?? code;
  const ar = new Intl.DisplayNames(["ar"], { type: "region" }).of(code) ?? code;
  return [code, {
    id: code, countryCode: code, currency: "",
    flag: String.fromCodePoint(...[...code].map((c) => c.charCodeAt(0) + 127397)),
    dialCode: isSupportedCountry(code) ? getCountryCallingCode(code) : null,
    supportsShipping: true, labels: { en, ar, fr: en },
  } satisfies Market];
})) as Record<AddedShippingCountry, Market>;

export const MARKETS: Record<MarketId, Market> = {
  ...addedMarkets,
  SA: {
    id: "SA",
    countryCode: "SA",
    // كتالوج السعودية في Shopify مسعَّر بالدرهم (AED) بأسعار مختلفة عن الإمارات،
    // لذا نعرض AED هنا حتى لا يتناقض ملصق المبدّل مع سعر Shopify الفعليّ.
    currency: "SAR",
    flag: "🇸🇦",
    dialCode: "966",
    supportsShipping: true,
    labels: { en: "Saudi Arabia", ar: "السعودية", fr: "Arabie Saoudite" },
  },
  AE: {
    id: "AE",
    countryCode: "AE",
    currency: "AED",
    flag: "🇦🇪",
    dialCode: "971",
    supportsShipping: true,
    labels: { en: "United Arab Emirates", ar: "الإمارات", fr: "Émirats" },
  },
  IQ: {
    id: "IQ",
    countryCode: "IQ",
    // USD لأنّ Stripe لا يدعم IQD، والدولار شائع في السوق العراقيّ
    currency: "AED",
    flag: "🇮🇶",
    dialCode: "964",
    supportsShipping: true,
    labels: { en: "Iraq", ar: "العراق", fr: "Irak" },
  },
  OM: {
    id: "OM",
    countryCode: "OM",
    currency: "AED",
    flag: "🇴🇲",
    dialCode: "968",
    supportsShipping: true,
    labels: { en: "Oman", ar: "عُمان", fr: "Oman" },
  },
  US: {
    id: "US",
    countryCode: "US",
    currency: "USD",
    flag: "🇺🇸",
    dialCode: "1",
    supportsShipping: true,
    labels: { en: "United States", ar: "الولايات المتحدة", fr: "États-Unis" },
  },
  KW: {
    id: "KW",
    countryCode: "KW",
    currency: "AED",
    flag: "🇰🇼",
    dialCode: "965",
    supportsShipping: true,
    labels: { en: "Kuwait", ar: "الكويت", fr: "Koweït" },
  },
  QA: {
    id: "QA",
    countryCode: "QA",
    // USD لأنّ بوابة الدفع لا تدعم QAR — نعرض الأسعار بالدولار
    currency: "QAR",
    flag: "🇶🇦",
    dialCode: "974",
    supportsShipping: true,
    labels: { en: "Qatar", ar: "قطر", fr: "Qatar" },
  },
  BH: {
    id: "BH",
    countryCode: "BH",
    currency: "AED",
    flag: "🇧🇭",
    dialCode: "973",
    supportsShipping: true,
    labels: { en: "Bahrain", ar: "البحرين", fr: "Bahreïn" },
  },
  ROW: {
    id: "ROW",
    // GB = market محايد للعرض الدوليّ. ممنوع استخدام "US" هنا لأنّ ذلك يجعل
    // ROW يرث أسعار سوق أمريكا حرفياً عبر Shopify @inContext، وأسعار US
    // مستقلّة تماماً (USD محليّ مع شحن داخليّ وضرائب ولاية).
    // اضبط Market «International» منفصل في Shopify Admin بـ GB → USD بسعر
    // دوليّ (عادة أعلى من US لشحن دوليّ مرتفع).
    countryCode: "GB",
    currency: "AED",
    flag: "🌍",
    dialCode: null,
    supportsShipping: false,
    labels: { en: "Rest of World", ar: "بقيّة العالم", fr: "Reste du monde" },
  },
};

export const MARKET_LIST: Market[] = [
  MARKETS.SA,
  MARKETS.AE,
  MARKETS.KW,
  MARKETS.QA,
  MARKETS.BH,
  MARKETS.OM,
  MARKETS.IQ,
  MARKETS.US,
  ...ADDED_SHIPPING_COUNTRIES.map((code) => MARKETS[code]),
  MARKETS.ROW,
];

/** يحوّل أيّ كود دولة ISO إلى أحد أسواقنا الأربعة، أو ROW. */
export function marketFromCountryCode(code: string | null | undefined): MarketId {
  if (!code) return "AE";
  const c = code.toUpperCase();
  if (SUPPORTED_MARKET_IDS.includes(c as MarketId)) return c as MarketId;
  return "ROW";
}

export function detectInitialMarket(): MarketId {
  if (typeof navigator === "undefined") return "AE";
  const lang = (navigator.language || "").toLowerCase();
  const region = lang.split(/[-_]/)[1]?.toUpperCase();
  if (region) return marketFromCountryCode(region);
  if (lang.startsWith("ar")) return "SA";
  return "ROW";
}

/** هل يمكن للسوق الحاليّ إتمام الشراء؟ */
export function canCheckoutFromMarket(id: MarketId): boolean {
  return MARKETS[id]?.supportsShipping === true;
}

/**
 * Gift destinations whose Shopify market was verified ACTIVE (read-only merchant
 * check, 2026-10-09): AE·AED, SA·SAR, QA·QAR, OM·AED, IQ·AED, US·USD.
 * KW/BH are selectable in the UI but NOT active in Shopify, so gifting there
 * must fail clearly instead of pricing against a fallback market.
 * A UI option alone is never proof of an active market — update only after
 * re-verifying Shopify Markets.
 */
export const ACTIVE_GIFT_MARKET_IDS: MarketId[] = ["AE", "SA", "QA", "OM", "IQ", "US", "CA"];

export function isGiftDestinationActive(codeOrId: string | null | undefined): boolean {
  if (!codeOrId) return false;
  return ACTIVE_GIFT_MARKET_IDS.includes(marketFromCountryCode(codeOrId));
}
